import {
  Box,
  MenuItem,
  TableBody,
  TableCell,
  TableRow,
  ThemeProvider,
  Toolbar,
  createTheme
} from "@mui/material";
import React, { useEffect } from "react";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import AdminSearch from "../AdminSearch";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import { useConfirmation } from "../../../shared/components/confirmation/ConfirmationProvider";
import {
  AdminOffer,
  AdminOffersQueryParameter,
  AdminOffersState,
  isAdminOffersQueryEmpty
} from "../../models/AdminOffersModels";
import {
  changeQuery,
  deleteOffer,
  getOfferDocument,
  getOffers,
  setError,
  setSuccess
} from "../../adminOffersSlice";
import ActionToast from "../../../shared/components/ActionToast";
import SortTableHead, { TableColumnDefinition } from "../../../shared/components/SortTableHead";
import Loader from "../../../shared/components/Loader";
import TableEmpty from "../../../shared/components/tables/TableEmpty";
import BoxSearchIcon from "../../../shared/assets/icons/BoxSearchIcon";
import Table from "../../../shared/components/tables/Table";
import Moment from "react-moment";
import { NumericFormat } from "react-number-format";
import TableActionButton from "../../../shared/components/tables/TableActionButton";
import { NotificationMessage, getName } from "../../../shared/models/models";
import TablePagination from "../../../shared/components/tables/TablePagination";
import DownloadFile from "../../../shared/components/DownloadFile";

const Offers = () => {
  const { query, offers, loading, success, error } = useAppSelector<AdminOffersState>(
    (state: any) => state.adminOffers
  );

  const dispatch = useAppDispatch();
  const confirm = useConfirmation();

  const columns: TableColumnDefinition[] = [
    { title: "Supplier",  parameter: AdminOffersQueryParameter.supplierName },
    { title: "Buyer",  parameter: AdminOffersQueryParameter.buyerName },
    { title: "Product" },
    { title: "Quantity" },
    { title: "Price/pc",  parameter: AdminOffersQueryParameter.price },
    // { title: "Shipping" },
    { title: "Offers details", parameter: AdminOffersQueryParameter.createdDate },
    { title: "Pdf-offer" },
    { title: undefined }
  ];

  const handleSearch = (value?: string) => {
    const newQuery = { ...query, search: value, pageNo: 1 };
    dispatch(changeQuery(newQuery));
  };

  const handleSort = async (parameter: number) => {
    const sortDesc = query.sortBy !== parameter ? false : !query.sortDesc;
    const newQuery = { ...query, sortBy: parameter, sortDesc: sortDesc };

    await dispatch(changeQuery(newQuery));
  };

  const handlePageSizeChange = async (size: number) => {
    const newQuery = { ...query, pageNo: 1, pageSize: size };
    await dispatch(changeQuery(newQuery));
  };

  const handlePageNoChange = async (no: number) => {
    const newQuery = { ...query, pageNo: no };
    await dispatch(changeQuery(newQuery));
  };

  const handleDeleteOffer = async (item: AdminOffer) => {
    if (item) {
      await dispatch(deleteOffer(item.id));
      await dispatch(getOffers(query));
    }
  };

  const isQueryEmpty = isAdminOffersQueryEmpty(query);

  useEffect(() => {
    dispatch(getOffers(query));
  }, [query]);

  const theme = createTheme({
    components: {
      MuiToolbar: {
        styleOverrides: {
          root: {
            background: "#fff",
            borderBottom: "1px solid #d5d6d9"
          }
        }
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            boderRadius: "6px 0 0 6px !important"
          }
        }
      }
    }
  });

  return (
    <ThemeProvider theme={theme}>
      <ActionToast
        open={!!success}
        variant="success"
        message={success || ""}
        onClose={() => dispatch(setSuccess(undefined))}
        duration={6000}
      />

      <ActionToast
        open={!!error}
        variant="error"
        message={NotificationMessage.error(error).message || ""}
        onClose={() => dispatch(setError(undefined))}
        duration={6000}
      />

      <Toolbar className="flex items-center justify-between w-full">
        <Box className="flex items-center">
          <Box className="heading-2">Offers</Box>
          <Box className="ml-7">
            <AdminSearch
              value={query.search}
              onSearch={handleSearch}
              placeholder="Type company name or product number"
            />
          </Box>
        </Box>
      </Toolbar>

      <Box className="px-4 v-fill">
        {loading ? (
          <Loader />
        ) : !offers?.items.length ? (
          <TableEmpty
            iconComponent={BoxSearchIcon}
            text={isQueryEmpty ? "No offers in the system" : "No such offers"}
          ></TableEmpty>
        ) : (
          <Table>
            <SortTableHead columns={columns} sortBy={query.sortBy} sortDesc={query.sortDesc} onSort={handleSort} />

            <TableBody>
              {offers?.items.map((item) => {
                return (
                  <TableRow key={item.id}>
                    <TableCell className="break-all" sx={{ maxWidth: "250px" }}>
                      <Box className="text-14-bold" color={"#333333"}>
                        {item.supplier.name}
                      </Box>
                    </TableCell>

                    <TableCell className="break-all" sx={{ maxWidth: "250px" }}>
                      <Box className="text-14-bold" color={"#747881"}>
                      {item.buyer.name}
                      </Box>
                    </TableCell>

                    <TableCell sx={{ width: "25%" }}>
                      <Box className="block">
                        <Box className="text-12-medium" color={"#333333"} sx={{ wordBreak: "break-all" }}>
                          {item.productNumber}
                        </Box>

                        <Box className="text-12-regular" sx={{ wordBreak: "break-all" }}>
                          {item.productAlternativeNumber}
                        </Box>

                        <Box className="mt-2">
                          <Box className="text-12-medium" color={"#333333"}>
                            {item.manufacturer.name}
                          </Box>
                          <Box className="text-12-regular" color={"#747881"}>
                            {item.category.name}
                          </Box>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Box className="text-14-medium" color={"#333333"}>
                        {item.quantity}
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Box className="text-14-medium" color={"#333333"}>
                        <NumericFormat
                          value={item.unitPrice.value}
                          displayType="text"
                          thousandSeparator=" "
                          suffix="€"
                        />
                      </Box>
                    </TableCell>

                    {/* <TableCell>Shipping</TableCell> */}

                    <TableCell sx={{ minWidth: "200px" }}>
                      <Box>
                        <Box className="text-12-regular" color={"#747881"}>
                          Created on{"\u00A0"}
                          <Moment date={item.createdDate} format="DD.MM.YYYY" />
                        </Box>

                        <Box className="text-12-regular" color={"#747881"}>
                          {"RFQ #" + item.quotationRequestNumber}
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <DownloadFile item={item} title="Download offer in .pdf" request={getOfferDocument}/>
                    </TableCell>

                    <TableCell>
                      <TableActionButton stopPropagation={true}>
                        <MenuItem
                          onClick={() =>
                            confirm({
                              title: "Delete Offer",
                              text: `Are you sure you want to delete ${getName(
                                item.manufacturer.name,
                                item.productNumber
                              )} offer?`,
                              confirmButton: "Yes, delete",
                              closeButton: "No",
                              severity: "error",
                              onConfirm: () => handleDeleteOffer(item)
                            })
                          }
                        >
                          <DeleteOutlinedIcon />
                          Delete
                        </MenuItem>
                      </TableActionButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Box>

      <TablePagination
        variant="bottom"
        pageNo={query.pageNo}
        pageSize={query.pageSize}
        totalCount={offers?.totalCount}
        onPageNoChange={handlePageNoChange}
        onPageSizeChange={handlePageSizeChange}
      />
    </ThemeProvider>
  );
};
export default Offers;